import { useState } from "react"
import { router } from "@inertiajs/react"
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { PipeStatusBadge } from "@/components/projects/pipe-status-badge"
import { PipeStatusFormDialog } from "@/components/pipe-status-form-dialog"
import { STATUS_CONFIG } from "@/lib/project-configs"
import { ChevronDown, GripVertical, Pencil, Plus, Trash2, Copy } from "lucide-react"
import type { PipeStatusRecord, ProjectStatus } from "@/types"

type Props = {
  pipeStatuses: PipeStatusRecord[]
  hasCustomPipeStatuses: boolean
  canManage: boolean
}

const PARENT_STATUSES: ProjectStatus[] = ["pending", "accepted", "rejected", "asset", "archived"]

function SortableStatusRow({
  ps,
  canManage,
  onEdit,
  onRequestDelete,
}: {
  ps: PipeStatusRecord
  canManage: boolean
  onEdit: (ps: PipeStatusRecord) => void
  onRequestDelete: (ps: PipeStatusRecord) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ps.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 py-2.5">
      {canManage ? (
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none">
          <GripVertical className="size-4 shrink-0 text-muted-foreground/50" />
        </button>
      ) : (
        <GripVertical className="size-4 shrink-0 text-muted-foreground/50" />
      )}
      <PipeStatusBadge pipeStatus={ps} />
      <span className="text-xs text-muted-foreground font-mono">{ps.name}</span>
      <div className="ml-auto flex items-center gap-1">
        {canManage && (
          <>
            <Button variant="ghost" size="icon-sm" onClick={() => onEdit(ps)}>
              <Pencil className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-destructive hover:text-destructive"
              onClick={() => onRequestDelete(ps)}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export function PipeStatusManager({ pipeStatuses, hasCustomPipeStatuses, canManage }: Props) {
  const [showForm, setShowForm] = useState(false)
  const [editingStatus, setEditingStatus] = useState<PipeStatusRecord | undefined>()
  const [defaultParent, setDefaultParent] = useState<ProjectStatus>("pending")
  const [deletingStatus, setDeletingStatus] = useState<PipeStatusRecord | null>(null)
  const [reassignToId, setReassignToId] = useState<string>("")
  const [localOrder, setLocalOrder] = useState<Record<ProjectStatus, PipeStatusRecord[]> | null>(null)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  // Group by parent status
  const byParent: Record<ProjectStatus, PipeStatusRecord[]> = {
    pending: [],
    accepted: [],
    rejected: [],
    asset: [],
    archived: [],
  }
  for (const ps of pipeStatuses) {
    if (byParent[ps.parent_status]) {
      byParent[ps.parent_status].push(ps)
    }
  }
  for (const key of PARENT_STATUSES) {
    byParent[key].sort((a, b) => a.position - b.position)
  }

  // Use localOrder if we have a pending reorder, otherwise use server data
  const getGroupItems = (status: ProjectStatus) => localOrder?.[status] || byParent[status]

  const handleDragEnd = (status: ProjectStatus) => (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const items = getGroupItems(status)
    const oldIndex = items.findIndex((ps) => ps.id === active.id)
    const newIndex = items.findIndex((ps) => ps.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(items, oldIndex, newIndex)

    // Update local state immediately for visual feedback
    setLocalOrder((prev) => ({ ...byParent, ...prev, [status]: reordered }))

    // Persist to backend
    router.patch(
      "/pipe_statuses/reorder",
      { ordered_ids: reordered.map((ps) => ps.id) },
      {
        preserveScroll: true,
        onSuccess: () => setLocalOrder(null),
        onError: () => setLocalOrder(null),
      },
    )
  }

  const handleInitialize = () => {
    router.post("/pipe_statuses/initialize_from_defaults", {}, { preserveScroll: true })
  }

  const handleRequestDelete = (ps: PipeStatusRecord) => {
    const siblings = byParent[ps.parent_status].filter((s) => s.id !== ps.id)
    if (siblings.length === 0) {
      toast.error("Vous devez conserver au moins un statut par catégorie")
      return
    }
    setDeletingStatus(ps)
    setReassignToId(siblings[0].id)
  }

  const handleDelete = () => {
    if (!deletingStatus) return
    const hasProjects = (deletingStatus.projects_count ?? 0) > 0
    router.delete(`/pipe_statuses/${deletingStatus.id}`, {
      data: hasProjects ? { reassign_to_id: reassignToId } : {},
      preserveScroll: true,
      onSuccess: () => {
        setDeletingStatus(null)
        setReassignToId("")
      },
    })
  }

  const deletingSiblings = deletingStatus
    ? byParent[deletingStatus.parent_status].filter((s) => s.id !== deletingStatus.id)
    : []
  const deletingHasProjects = (deletingStatus?.projects_count ?? 0) > 0

  const handleAdd = (parentStatus: ProjectStatus) => {
    setEditingStatus(undefined)
    setDefaultParent(parentStatus)
    setShowForm(true)
  }

  const handleEdit = (ps: PipeStatusRecord) => {
    setEditingStatus(ps)
    setDefaultParent(ps.parent_status)
    setShowForm(true)
  }

  if (!hasCustomPipeStatuses) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Statuts pipeline par défaut</CardTitle>
            <CardDescription>
              Votre organisation utilise les statuts pipeline par défaut. Personnalisez-les pour adapter le pipeline à
              votre workflow.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {PARENT_STATUSES.map((status) => {
                const pipes = byParent[status]
                if (pipes.length === 0) return null
                const config = STATUS_CONFIG[status]
                return (
                  <div key={status}>
                    <p className="text-xs font-medium text-muted-foreground mb-1">{config.label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {pipes.map((ps) => (
                        <PipeStatusBadge key={ps.id} pipeStatus={ps} />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
            {canManage && (
              <Button onClick={handleInitialize} className="mt-4">
                <Copy className="mr-2 size-4" />
                Personnaliser les statuts
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {PARENT_STATUSES.map((status) => {
        const pipes = getGroupItems(status)
        const config = STATUS_CONFIG[status]
        const Icon = config.icon

        return (
          <Collapsible key={status} defaultOpen>
            <Card>
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-muted/50 transition-colors rounded-t-xl">
                <div className="flex items-center gap-2">
                  <Icon className="size-4" style={{ color: config.color }} />
                  <span className="text-sm font-medium">{config.label}</span>
                  <span className="text-xs text-muted-foreground">({pipes.length})</span>
                </div>
                <ChevronDown className="size-4 text-muted-foreground transition-transform [[data-state=open]>&]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="border-t px-4 pb-4">
                  {pipes.length === 0 ? (
                    <p className="py-3 text-sm text-muted-foreground">Aucun statut</p>
                  ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd(status)}>
                      <SortableContext items={pipes.map((ps) => ps.id)} strategy={verticalListSortingStrategy}>
                        <div className="divide-y">
                          {pipes.map((ps) => (
                            <SortableStatusRow
                              key={ps.id}
                              ps={ps}
                              canManage={canManage}
                              onEdit={handleEdit}
                              onRequestDelete={handleRequestDelete}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                  {canManage && (
                    <Button variant="outline" size="sm" className="mt-2" onClick={() => handleAdd(status)}>
                      <Plus className="mr-1 size-3.5" />
                      Ajouter un statut
                    </Button>
                  )}
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )
      })}

      {/* Form dialog */}
      <PipeStatusFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        pipeStatus={editingStatus}
        defaultParentStatus={defaultParent}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!deletingStatus} onOpenChange={(open) => { if (!open) { setDeletingStatus(null); setReassignToId("") } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le statut</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingHasProjects
                ? `Le statut « ${deletingStatus?.label} » est utilisé par ${deletingStatus?.projects_count} projet(s). Choisissez un statut de remplacement.`
                : `Êtes-vous sûr de vouloir supprimer le statut « ${deletingStatus?.label} » ?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deletingHasProjects && deletingSiblings.length > 0 && (
            <div className="px-6 pb-2">
              <label className="text-sm font-medium mb-1.5 block">Réaffecter les projets vers</label>
              <Select value={reassignToId} onValueChange={setReassignToId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {deletingSiblings.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block size-2.5 shrink-0 rounded-sm"
                          style={{ backgroundColor: s.bg_color }}
                        />
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deletingHasProjects && !reassignToId}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
